import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ADD_PRODUCTS_ROUTE, ADD_PRODUCTS_SEARCH_ROUTE, FeatureToggleEnum } from '@mwe/constants';
import {
  ActionType,
  AddressGroup,
  AddressProducts,
  ContractStatusEnum,
  EnergyVoucherMapping,
  ILSystem,
  IVerknuepfungen,
  IVerknuepfungenResponse,
  Product,
  RecentOrder,
} from '@mwe/models';
import {
  checkAddressProductsForAddressGroupUpdates,
  cloneDeepJSON,
  getCommonMappingDataGroupedByAccountNumber,
  isArrayWithMinOneItem,
  isContractStatus,
  parseRecentOrdersDTO,
  parseVerknuepfungen,
  SessionStorageService,
} from '@mwe/utils';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration.js';
import { catchError, map, mergeMap, toArray } from 'rxjs/operators';
import { AccountLogic } from '../account/account.logic';
import { AppStorageService } from '../cache/app-storage.service';
import { LoggingService } from '../logging/logging.service';
import { ProfileService } from '../profile/profile.service';
import { ProductStateService } from './product-state.service';
import { ProductService } from './product.service';
import { filter, forkJoin, from, lastValueFrom, of, switchMap, tap, throwError } from 'rxjs';
import { PRODUCTS_FILTER_STATE, productStore } from './product.store';
import { AddressGroupService } from '../address/address-group.service';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { LoadingService } from '../loading/loading.service';
import { ClearingAccountLogic } from '../invoices/clearing-account/clearing-account.logic';

dayjs.extend(duration);

@Injectable({
  providedIn: 'root',
})
export class ProductLogic {
  addProductsSearchRoute = `/${ADD_PRODUCTS_ROUTE}/${ADD_PRODUCTS_SEARCH_ROUTE}`;
  private associationsUpdates: IVerknuepfungenResponse;

  private accountLogic = inject(AccountLogic);
  private productService = inject(ProductService);
  private productStateService = inject(ProductStateService);
  private loggingService = inject(LoggingService);
  private profileService = inject(ProfileService);
  private router = inject(Router);
  private appStorage = inject(AppStorageService);
  private addressGroupService = inject(AddressGroupService);
  private productStore = inject(productStore);
  private loadingService = inject(LoadingService);
  private storageService = inject(SessionStorageService);
  private clearingAccountLogic = inject(ClearingAccountLogic);

  private addressGroups: AddressGroup[] = [];
  private loaded = false;

  removeNotMatchedGroups(addressGroups: AddressGroup[]) {
    if (!addressGroups?.length) return of(null);
    return from(addressGroups).pipe(
      filter(ag => !ag.matchedGroup),
      mergeMap(ag => {
        if (!ag?.id) return of(null);
        return fromPromise(this.addressGroupService.deleteAddressGroupName(ag)).pipe(
          map(() => {
            this.loggingService.logAction({
              type: ActionType.ADDRESS_GROUP,
              message: `no match found for address group ${ag.addressCode}, entry will be removed`,
            });
            return ag.id;
          }),
          catchError(_ => {
            this.loggingService.logError(new Error('Error while deleting group with id: ' + ag.id));
            return of(null);
          }),
        );
      }),
      toArray(),
      map(removedGroupIds => addressGroups?.filter(ag => !removedGroupIds?.includes(ag.id))),
    );
  }

  prepareProducts({ products, isRefresh }: { products?: Product[]; isRefresh?: boolean } = {}) {
    const baseProducts$ = products ? of(products) : fromPromise(this.getProducts(isRefresh));
    const products$ = baseProducts$.pipe(
      map(products => {
        const orderedProducts = this.orderAndGroupByAddresses(products);
        return {
          orderedProducts,
        };
      }),
    );

    const groupNames$ = isRefresh ? fromPromise(this.addressGroupService.getAndUpdateGroupNames()) : of<AddressGroup[]>(this.addressGroups);

    return this.loadingService.load({
      identifier: 'addressProducts',
      observables: {
        addressProducts: forkJoin([products$, groupNames$]).pipe(
          map(([{ orderedProducts }, groupNames]) => {
            this.addressGroups = groupNames;
            const updatedAddressGroups = checkAddressProductsForAddressGroupUpdates(orderedProducts, groupNames);
            this.productStore.setAddressGroups(updatedAddressGroups);
            return {
              addressGroups: updatedAddressGroups,
            };
          }),
          switchMap(() => {
            if (this.loaded) {
              return of(this.productStore.clearingAccounts());
            }
            this.loaded = true;
            return this.loadClearingAccounts();
          }),
          catchError(err => {
            return throwError(() => err);
          }),
        ),
      },
      deleteLoadingState: false,
      stateChangeDelay: 0,
    });
  }

  loadInitialProductsAfterLogin() {
    return this.prepareProducts({ isRefresh: true }).pipe(
      tap(() => this.setInitialFilterState()),
      switchMap(() => from(this.checkAssociationsUpdate())),
    );
  }

  private loadClearingAccounts() {
    return this.loadingService.load({
      identifier: 'clearingAccount',
      observables: {
        clearingAccount: fromPromise(this.clearingAccountLogic.getAllUnpaidInvoices(this.productStore.addressGroupsInternal())).pipe(
          tap(clearingAccounts => this.productStore.setClearingAccounts(clearingAccounts)),
          catchError(err => {
            return throwError(() => err);
          }),
        ),
      },
      deleteLoadingState: false,
      stateChangeDelay: 0,
    });
  }

  setInitialFilterState(ignoreSetState = false) {
    if (!this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.ENDED_CONTRACTS_ENABLED)) {
      return this.productStore.setCurrentState(this.productStore.currentState());
    }
    const savedState = ignoreSetState ? null : this.storageService.retrieve(PRODUCTS_FILTER_STATE);
    if (this.productStore.hasOnlyActiveProducts()) {
      return this.productStore.setCurrentState(savedState ?? this.productStore.filterButtonOptions()[0]);
    }
    if (this.productStore.hasOnlyInactiveProducts()) {
      return this.productStore.setCurrentState(savedState ?? this.productStore.filterButtonOptions()[1]);
    }

    this.productStore.setCurrentState(savedState ?? this.productStore.currentState());
  }

  parseVerknuepfungenFromFilteredAssociations(): Product[] {
    const filteredAssociations = this.getFilteredAssociationsWithVerknuepfungsStatus();
    return parseVerknuepfungen(filteredAssociations.verknuepfungen);
  }

  getFilteredAssociationsWithVerknuepfungsStatus(): IVerknuepfungenResponse {
    return this.productService.filterAssociationsWithValidVerknuepfungsStatus(this.productStore.associations());
  }

  async checkAssociationsUpdate(): Promise<boolean> {
    return this.compareAndUpdateAssociations();
  }

  async findPrivateProducts(kn: string, name: string, birthday: string): Promise<IVerknuepfungenResponse> {
    const response = await this.productService.findPrivateProducts(kn, name, birthday);
    return this.productService.filterAssociations(response);
  }

  async findBusinessStromGasProducts(customerId: string, systemId: ILSystem, accountId: string): Promise<IVerknuepfungenResponse> {
    const response = await this.productService.findBusinessStromGasProducts(customerId, systemId, accountId);
    return this.productService.filterAssociations(response);
  }

  async findBusinessWaermeProducts(customerId: string, systemId: ILSystem, organisationsName: string): Promise<IVerknuepfungenResponse> {
    const response = await this.productService.findBusinessWaermeProducts(customerId, systemId, organisationsName);
    return this.productService.filterAssociations(response);
  }

  clearAssociationsUpdate(): void {
    this.associationsUpdates = null;
  }

  getProductsPerAccountNumber(products: Product[]): Map<string, Product[]> {
    const productsPerAccountNumber: Map<string, Product[]> = new Map<string, Product[]>();
    products.forEach(product => {
      productsPerAccountNumber.set(product.accountNumber, [...(productsPerAccountNumber.get(product.accountNumber) || []), product]);
    });
    return productsPerAccountNumber;
  }

  orderAndGroupByAddresses(products: Product[]): AddressProducts[] {
    if (!Array.isArray(products)) {
      return [];
    }

    const sortedProducts = this.sortProductsByAddressHashcode(products);

    const addressGroups = sortedProducts?.reduce((addressGroups, product) => {
      const hashCode = product.address.hashCode;
      if (!hashCode?.length) return addressGroups;

      const existingGroup = addressGroups.get(hashCode) || { address: product.address, products: [] };
      const updatedGroup = {
        ...existingGroup,
        products: [...existingGroup.products, product],
      };

      return new Map(addressGroups).set(hashCode, updatedGroup);
    }, new Map<string, AddressProducts>());

    return Array.from(addressGroups.values());
  }

  getRecentOrders(): Promise<RecentOrder[]> {
    if (this.productStore.recentOrders()) {
      return Promise.resolve(this.productStore.recentOrders());
    }

    return lastValueFrom(
      this.productService.getRecentOrders().pipe(
        map(recentOrdersDTO => parseRecentOrdersDTO(recentOrdersDTO)),
        map(recentOrders => {
          this.productStore.setRecentOrders(recentOrders);
          return recentOrders;
        }),
      ),
    );
  }

  async getProductDataGroupedByAccountNumber(): Promise<EnergyVoucherMapping[]> {
    const products = await this.getProducts();
    const activeProducts = products.filter(p => isContractStatus(p.vertragsStatus, ContractStatusEnum.Aktiv));

    return getCommonMappingDataGroupedByAccountNumber(activeProducts);
  }

  refreshProducts() {
    return this.prepareProducts({ isRefresh: true }).pipe(
      switchMap(() => {
        return this.loadingService.load({
          identifier: 'reload',
          observables: {
            reload: fromPromise(this.checkAssociationsUpdate()),
          },
          stateChangeDelay: 0,
          deleteLoadingState: false,
        });
      }),
      tap(() => {
        this.setInitialFilterState(true);
      }),
      catchError(err => {
        return throwError(() => err);
      }),
    );
  }

  private async getProducts(isRefresh = false): Promise<Product[]> {
    if (this.productStore.associations() && !isRefresh) {
      return this.parseVerknuepfungenFromFilteredAssociations();
    }

    const response = await this.productService.loadAssociations();
    this.productStore.setAssociationsOrig(cloneDeepJSON(response) as IVerknuepfungenResponse);

    const filteredAssociations = this.productService.filterAssociations(response);
    this.productStore.setAssociations(filteredAssociations);

    return this.parseVerknuepfungenFromFilteredAssociations();
  }

  private async updateAssociationsAndFindMissing(associationsUpdates: IVerknuepfungenResponse): Promise<IVerknuepfungen[]> {
    const associationsOrig = cloneDeepJSON(this.productStore.associationsOrig()) as IVerknuepfungenResponse;

    const missingAssociations: IVerknuepfungen[] = [];

    for (let i = 0; i < associationsOrig.verknuepfungen.length; i++) {
      const updatedAssociation = associationsUpdates?.verknuepfungen?.find(
        association =>
          association.geschaeftsPartnerId === associationsOrig.verknuepfungen[i].geschaeftsPartnerId && association.produkte?.length,
      );

      if (updatedAssociation) {
        associationsOrig.verknuepfungen[i] = updatedAssociation;
      } else {
        missingAssociations.push(associationsOrig.verknuepfungen[i]);
      }
    }

    this.associationsUpdates = this.productService.filterAssociations(this.associationsUpdates);
    this.associationsUpdates = this.productService.filterAssociationsWithoutDeclinedVerknuepfungsStatus(this.associationsUpdates);
    this.productStore.setAssociationsOrig(associationsOrig);
    return missingAssociations;
  }

  private async findNewAssociations(associationsUpdates: IVerknuepfungenResponse): Promise<IVerknuepfungen[]> {
    const associationsOrig = this.productStore.associationsOrig();

    return associationsUpdates?.verknuepfungen?.filter(association => {
      const isViable = !associationsOrig?.verknuepfungen?.some(a => {
        return a.geschaeftsPartnerId === association?.geschaeftsPartnerId && association?.verknuepfungsStatus?.length > 0;
      });

      if (isViable) {
        // Filter associations with only ended contracts. They should not display in the association suggestion Popup
        return association?.produkte?.some(product => isContractStatus(product.vertragsStatus, ContractStatusEnum.Aktiv));
      }

      return isViable;
    });
  }

  private async logMissingAssociation(missingAssociation: IVerknuepfungen): Promise<void> {
    if (!this.productStateService.hasLoggedMissingAssociation(missingAssociation)) {
      this.loggingService.logAction({
        type: ActionType.ASSOCIATIONS,
        message: 'Missing association for ' + missingAssociation.geschaeftsPartnerId + '. Data: ' + JSON.stringify(missingAssociation),
      });
      this.productStateService.addLoggedMissingAssociation(missingAssociation);
    }
  }

  private async logNewAssociation(newAssociation: IVerknuepfungen): Promise<void> {
    if (!this.productStateService.hasLoggedNewAssociation(newAssociation)) {
      this.loggingService.logAction({
        type: ActionType.ASSOCIATIONS,
        message: 'New association for ' + newAssociation.geschaeftsPartnerId + '. Data: ' + JSON.stringify(newAssociation),
      });
      this.productStateService.addLoggedNewAssociation(newAssociation);
    }
  }

  private async logAssociationSuggestions(associationSuggestions: IVerknuepfungen[]): Promise<void> {
    associationSuggestions = associationSuggestions?.filter(
      association => !this.productStateService.hasLoggedAssociationSuggestion(association),
    );
    if (associationSuggestions?.length > 0) {
      const account = this.accountLogic.getAccount();
      this.loggingService.logAction({
        type: ActionType.ASSOCIATIONS,
        message: `[association-suggestion] found (${account.id} / ${account.email} / ${associationSuggestions
          .map(elem => elem.geschaeftsPartnerId)
          .join(',')})`,
      });
      for (const association of associationSuggestions) {
        this.productStateService.addLoggedAssociationSuggestion(association);
      }
    }
  }

  private async compareAndUpdateAssociations(): Promise<boolean> {
    try {
      const email = this.accountLogic.getAccountEmail();
      if (email === undefined || email === null) {
        throw new Error('Missing email address!');
      }

      if (!this.associationsUpdates) {
        this.associationsUpdates = await this.productService.getAssociationsUpdates(email);
      }

      let associationsUpdates = this.associationsUpdates;
      if (!isArrayWithMinOneItem(associationsUpdates?.verknuepfungen)) {
        return false;
      }

      const missingAssociations = await this.updateAssociationsAndFindMissing(associationsUpdates);
      const associationsOrig = this.productStore.associationsOrig();
      for (const missingAssociation of missingAssociations) {
        await this.logMissingAssociation(missingAssociation);
      }

      // disassociate associationsUpdates from original to avoid changing references in associationsOrig through filter side effects
      associationsUpdates = cloneDeepJSON(associationsUpdates) as IVerknuepfungenResponse;
      associationsUpdates = this.productService.filterAssociations(associationsUpdates);
      associationsUpdates = this.productService.filterAssociationsWithoutDeclinedVerknuepfungsStatus(associationsUpdates);

      const newAssociationsRes = await this.findNewAssociations(associationsUpdates);

      const newAssociations = newAssociationsRes?.filter(association => association != null);
      if (
        this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.ASSOCIATION_SUGGESTIONS_ENABLED) &&
        !this.accountLogic.isImpersonate()
      ) {
        this.productStateService.associationSuggestions = newAssociations;
        this.logAssociationSuggestions(newAssociations);
      } else {
        for (const newAssociation of newAssociations) {
          await this.logNewAssociation(newAssociation);
          if (this.appStorage.isEmailInvitation()) {
            await this.router.navigate([this.addProductsSearchRoute]);
          }
          return false;
        }
      }

      if (0 === newAssociations?.length && this.appStorage.isEmailInvitation()) {
        await this.router.navigate([this.addProductsSearchRoute]);
      }
      const associationsWithVerknuepfungsStatus = this.productService.filterAssociationsWithValidVerknuepfungsStatus(associationsOrig);
      const filteredAssociations = this.productService.filterAssociations(associationsWithVerknuepfungsStatus);
      this.productStore.setAssociations(filteredAssociations);
      const products = this.parseVerknuepfungenFromFilteredAssociations();
      this.prepareProducts({ products }).subscribe();
      return true;
    } catch (error) {
      this.loggingService.logError(error, 'error in compareAndUpdateAssociations');
      return false;
    }
  }

  private sortProductsByAddressHashcode(products: Product[]) {
    return products.toSorted((a, b) => {
      if (a.address?.hashCode === b.address?.hashCode) {
        return 0;
      }

      return a.address?.hashCode > b.address?.hashCode ? 1 : -1;
    });
  }
}
